import { useEffect, useState } from 'react';
import { ApiError, inviteApi, type InviteAdmin, type InviteInfo, type MemberInfo, type NewInvite, type OfferPick } from '../api';

/** Uitnodigingen (alleen de eigenaar): links maken, aanmeldingen goedkeuren, en "alleen met uitnodiging". */
export function InviteSettings() {
  const [d, setD] = useState<InviteAdmin | null>(null);
  const [denied, setDenied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tempPw, setTempPw] = useState<{ id: string; pw: string } | null>(null);
  const [fresh, setFresh] = useState<InviteInfo | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => inviteApi.admin().then(setD).catch((err) => { if (err instanceof ApiError && err.status === 403) setDenied(true); });
  useEffect(() => {
    void load();
  }, []);

  if (denied || !d) return null;

  const act = async (fn: () => Promise<unknown>) => {
    setMsg(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Dat lukte niet.');
    }
  };

  const pending = d.members.filter((m) => m.status === 'pending');
  const inviteOf = (m: MemberInfo) => d.invites.find((i) => i.id === m.inviteId);

  return (
    <section className="settings-block" aria-label="Uitnodigingen">
      <h2>Uitnodigingen</h2>
      <p className="subtle hint">
        Nodig iemand uit met een link. Na het aanmelden logt die persoon in met Plex en loopt een korte welkomst-wizard door
        (Live TV, addons, muziek, sport, taal en uiterlijk). Jij als eigenaar komt altijd binnen, en wie al eens heeft ingelogd blijft welkom.
      </p>

      <label className="studio-toggle inv-switch">
        <span>
          <b>Alleen met uitnodiging</b>
          <small className="subtle">Nieuwe Plex-gebruikers hebben dan een uitnodiging nodig om tumedia te gebruiken.</small>
        </span>
        <span className="toggle"><input type="checkbox" checked={d.required} onChange={(e) => void act(() => inviteApi.setRequired(e.target.checked))} /></span>
      </label>
      {d.envOff && <p className="status-pill warn">Staat uit via INVITES=off in Docker: iedereen met Plex-toegang komt binnen.</p>}

      {pending.length > 0 && (
        <div className="inv-pending" role="status">
          <b>{pending.length === 1 ? '1 aanmelding wacht' : `${pending.length} aanmeldingen wachten`} op goedkeuring</b>
        </div>
      )}

      {fresh && <FreshLink invite={fresh} onClose={() => setFresh(null)} />}

      {creating ? (
        <InviteForm
          d={d}
          onCancel={() => setCreating(false)}
          onCreated={(inv) => { setCreating(false); setFresh(inv); void load(); }}
        />
      ) : (
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={() => { setCreating(true); setFresh(null); }}>Nieuwe uitnodiging</button>
          <a className="btn" href="/welkom">Welkomst-wizard bekijken</a>
        </div>
      )}
      {msg && <p className="error" role="alert">{msg}</p>}

      {d.members.length > 0 && (
        <>
          <h3 className="inv-h">Aanmeldingen</h3>
          <ul className="inv-list">
            {d.members.map((m) => (
              <li key={m.id} className="inv-row">
                <div className="inv-main">
                  <b>{m.name}</b>
                  <small className="subtle">
                    {m.email}
                    {m.kind === 'local'
                      ? ` · zonder Plex${m.plexLinked ? ` (Plex gekoppeld: ${m.plexLinked})` : ''}${m.profiles ? ` · ${m.profiles} profiel${m.profiles === 1 ? '' : 'en'}` : ''}${m.lastLogin ? '' : ' · nog niet ingelogd'}`
                      : m.plexUsername ? ` · Plex: ${m.plexUsername}` : ' · nog niet ingelogd'}
                    {inviteOf(m)?.label ? ` · ${inviteOf(m)!.label}` : ''}
                  </small>
                  {tempPw?.id === m.id && <small className="inv-temp-pw">Tijdelijk wachtwoord: <b>{tempPw.pw}</b> — geef dit door; bij inloggen kiest {m.name} zelf een nieuw.</small>}
                  {m.share === 'failed' && <small className="error">Automatisch delen in Plex mislukt{m.shareError ? `: ${m.shareError}` : ''}. Deel de bibliotheek dan zelf in Plex.</small>}
                </div>
                <span className={`status-pill ${m.status === 'active' ? 'ok' : m.status === 'blocked' ? 'bad' : 'warn'}`}>
                  {m.status === 'active' ? 'Actief' : m.status === 'pending' ? 'Wacht op goedkeuring' : m.status === 'blocked' ? 'Geblokkeerd' : 'Aangemeld'}
                </span>
                {m.share === 'shared' && <span className="status-pill ok">Plex gedeeld</span>}
                <div className="inv-actions">
                  {(m.status === 'pending' || m.status === 'blocked') && m.accountId !== null && (
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => void act(() => inviteApi.approve(m.id))}>{m.status === 'blocked' ? 'Weer toelaten' : 'Goedkeuren'}</button>
                  )}
                  {m.status !== 'blocked' && m.accountId !== null && (
                    <button type="button" className="btn btn-sm" onClick={() => { if (confirmDo(`${m.name} blokkeren? Diegene wordt meteen uitgelogd.`)) void act(() => inviteApi.block(m.id)); }}>Blokkeren</button>
                  )}
                  {m.kind === 'local' && (
                    <button type="button" className="btn btn-sm" onClick={() => { if (confirmDo(`Nieuw tijdelijk wachtwoord voor ${m.name}? Het oude werkt dan niet meer.`)) void inviteApi.resetPassword(m.id).then((r) => setTempPw({ id: m.id, pw: r.password })).catch(() => undefined); }}>Wachtwoord resetten</button>
                  )}
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => { if (confirmDo(m.kind === 'local' ? `${m.name} verwijderen? Het account zonder Plex (met profielen en lijst) verdwijnt helemaal.` : `Aanmelding van ${m.name} verwijderen?`)) void act(() => inviteApi.removeMember(m.id)); }}>Verwijderen</button>
                </div>
              </li>
            ))}
          </ul>
          <p className="subtle hint">Blokkeren sluit iemand buiten tumedia. De gedeelde bibliotheek in Plex zelf haal je weg in Plex (Instellingen → Gebruikers).</p>
        </>
      )}

      {d.invites.length > 0 && (
        <>
          <h3 className="inv-h">Links</h3>
          <ul className="inv-list">
            {d.invites.map((i) => (
              <li key={i.id} className={`inv-row${i.usable ? '' : ' is-off'}`}>
                <div className="inv-main">
                  <b>{i.label || 'Uitnodiging'} <code>{i.code}</code></b>
                  <small className="subtle">
                    {d.templates.find((t) => t.id === i.template)?.name ?? i.template}
                    {' · '}{i.maxUses === null ? `${i.uses}× gebruikt` : `${i.uses}/${i.maxUses} gebruikt`}
                    {' · '}{i.revoked ? 'ingetrokken' : i.expiresAt ? (i.expiresAt < Date.now() ? 'verlopen' : `geldig tot ${new Date(i.expiresAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`) : 'geen einddatum'}
                    {i.access === 'addons' ? ' · zonder Plex' : i.access === 'choice' ? ' · met of zonder Plex' : ''}{i.sources?.debrid ? ' · debrid' : ''}{i.sources?.usenet ? ' · usenet' : ''}{i.needsApproval ? ' · met goedkeuring' : ''}{i.share ? ' · deelt Plex' : ''}{i.addons.length ? ` · ${i.addons.length} addon${i.addons.length === 1 ? '' : 's'}` : ''}
                  </small>
                </div>
                <div className="inv-actions">
                  {i.usable && <CopyButton text={`${location.origin}${i.link}`} />}
                  {!i.revoked && <button type="button" className="btn btn-sm btn-ghost" onClick={() => void act(() => inviteApi.revoke(i.id))}>Intrekken</button>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function confirmDo(text: string): boolean {
  // Gewone bevestiging; op tv's zonder dialoog gewoon doorgaan
  try {
    return window.confirm(text);
  } catch {
    return true;
  }
}

function CopyButton({ text, label = 'Link kopiëren' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      window.setTimeout(() => setDone(false), 2000);
    } catch {
      window.prompt('Kopieer de link:', text);
    }
  };
  return <button type="button" className="btn btn-sm" onClick={() => void copy()}>{done ? '✓ Gekopieerd' : label}</button>;
}

function FreshLink({ invite, onClose }: { invite: InviteInfo; onClose: () => void }) {
  const url = `${location.origin}${invite.link}`;
  const canShare = typeof navigator.share === 'function';
  return (
    <div className="inv-fresh" role="status">
      <b>Uitnodiging klaar{invite.label ? ` voor ${invite.label}` : ''}</b>
      <p className="subtle">Stuur deze link naar de persoon die je uitnodigt. De code staat er al in.</p>
      <div className="inv-url"><input className="text-input" readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Uitnodigingslink" /></div>
      <div className="form-actions">
        <CopyButton text={url} />
        {canShare && <button type="button" className="btn btn-sm" onClick={() => void navigator.share({ title: 'Uitnodiging voor tumedia', text: 'Je bent uitgenodigd voor tumedia:', url }).catch(() => undefined)}>Delen…</button>}
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Sluiten</button>
      </div>
    </div>
  );
}

function InviteForm({ d, onCancel, onCreated }: { d: InviteAdmin; onCancel: () => void; onCreated: (i: InviteInfo) => void }) {
  const [f, setF] = useState<NewInvite>({ label: '', days: 7, maxUses: 1, template: 'gast', needsApproval: false, share: true, sectionIds: [], addons: [], access: 'plex', sources: d.templates.find((t) => t.id === 'gast')?.sources ?? { addons: true, debrid: true, usenet: false } });
  const [addons, setAddons] = useState<{ id: string; name: string; kind: 'video' | 'music'; icon: string | null; setupUrl: string | null }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    inviteApi.ownAddons().then((r) => setAddons(r.addons)).catch(() => setAddons([]));
  }, []);
  const set = <K extends keyof NewInvite>(k: K, v: NewInvite[K]) => setF((x) => ({ ...x, [k]: v }));
  const toggleIn = (k: 'sectionIds', id: string) => setF((x) => ({ ...x, [k]: x[k].includes(id) ? x[k].filter((y) => y !== id) : [...x[k], id] }));
  const pickOf = (id: string) => f.addons.find((p) => p.id === id);
  const toggleAddon = (a: { id: string; setupUrl: string | null }) => setF((x) => ({
    ...x,
    addons: x.addons.some((p) => p.id === a.id) ? x.addons.filter((p) => p.id !== a.id) : [...x.addons, { id: a.id, mode: 'recommend', setupUrl: a.setupUrl }],
  }));
  const patchAddon = (id: string, patch: Partial<OfferPick>) => setF((x) => ({ ...x, addons: x.addons.map((p) => (p.id === id ? { ...p, ...patch } : p)) }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await inviteApi.create(f);
      onCreated(r.invite);
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Maken mislukt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="inv-form form-grid" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label>Voor wie (alleen voor jou zichtbaar)<input className="text-input" value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="bijv. Mark" maxLength={60} /></label>
      <label>
        Rechten
        <select className="text-input" value={f.template} onChange={(e) => {
          const t = d.templates.find((x) => x.id === e.target.value);
          setF((x) => ({ ...x, template: e.target.value, sources: t?.sources ?? x.sources }));
        }}>
          {d.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </label>
      <label>
        Geldig
        <select className="text-input" value={f.days ?? 0} onChange={(e) => set('days', Number(e.target.value) || null)}>
          <option value={1}>1 dag</option>
          <option value={7}>7 dagen</option>
          <option value={30}>30 dagen</option>
          <option value={0}>Geen einddatum</option>
        </select>
      </label>
      <label>
        Te gebruiken door
        <select className="text-input" value={f.maxUses ?? 0} onChange={(e) => set('maxUses', Number(e.target.value) || null)}>
          <option value={1}>1 persoon</option>
          <option value={5}>Tot 5 personen</option>
          <option value={0}>Onbeperkt</option>
        </select>
      </label>
      <label>
        Gebruikt tumedia met
        <select className="text-input" value={f.access} onChange={(e) => set('access', e.target.value as NewInvite['access'])}>
          <option value="plex">Plex-account (jouw bibliotheek)</option>
          <option value="addons">Zonder Plex: addons en debrid (e-mail en wachtwoord)</option>
          <option value="choice">Zelf kiezen (met of zonder Plex)</option>
        </select>
      </label>
      <fieldset className="inv-fieldset">
        <legend className="subtle">Bronnen die deze persoon mag gebruiken</legend>
        <label className="check-row"><input type="checkbox" checked={f.sources.addons || f.sources.debrid} disabled={f.sources.debrid} onChange={(e) => set('sources', { ...f.sources, addons: e.target.checked })} /> Addons <small className="subtle">(eigen Stremio-addons toevoegen)</small></label>
        <label className="check-row"><input type="checkbox" checked={f.sources.debrid} onChange={(e) => set('sources', { ...f.sources, debrid: e.target.checked, addons: e.target.checked || f.sources.addons })} /> Debrid <small className="subtle">(eigen TorBox- of Real-Debrid-sleutel; kost deze server niets)</small></label>
        <label className="check-row"><input type="checkbox" checked={f.sources.usenet} onChange={(e) => set('sources', { ...f.sources, usenet: e.target.checked })} /> Usenet <small className="subtle">(loopt via deze server; werkt zodra Usenet is ingesteld)</small></label>
        {f.sources.debrid && <p className="subtle hint">In de wizard vraagt tumedia meteen om de debrid-sleutel te koppelen.</p>}
      </fieldset>
      <label className="check-row"><input type="checkbox" checked={f.needsApproval} onChange={(e) => set('needsApproval', e.target.checked)} /> Eerst door mij laten goedkeuren</label>
      <label className="check-row"><input type="checkbox" checked={f.share} onChange={(e) => set('share', e.target.checked)} /> Plex-bibliotheken automatisch delen</label>
      {f.share && (
        d.sections?.length ? (
          <fieldset className="inv-fieldset">
            <legend className="subtle">Welke bibliotheken? (niets aangevinkt = alle)</legend>
            {d.sections.map((s) => (
              <label key={s.id} className="check-row"><input type="checkbox" checked={f.sectionIds.includes(s.id)} onChange={() => toggleIn('sectionIds', s.id)} /> {s.title}</label>
            ))}
          </fieldset>
        ) : (
          <p className="subtle hint">Alle bibliotheken worden gedeeld.</p>
        )
      )}
      {addons.length > 0 && (
        <fieldset className="inv-fieldset">
          <legend className="subtle">Addons aanbieden in de wizard</legend>
          {addons.map((a) => {
            const pick = pickOf(a.id);
            return (
              <div key={a.id} className="inv-addon">
                <label className="check-row"><input type="checkbox" checked={!!pick} onChange={() => toggleAddon(a)} /> {a.name} <small className="subtle">({a.kind === 'music' ? 'muziek' : 'video'})</small></label>
                {pick && (
                  <div className="inv-addon-opts">
                    <div className="seg" role="radiogroup" aria-label={`Hoe ${a.name} aanbieden`}>
                      <button type="button" role="radio" aria-checked={pick.mode === 'recommend'} className={pick.mode === 'recommend' ? 'is-on' : ''} onClick={() => patchAddon(a.id, { mode: 'recommend' })}>Aanraden · eigen sleutels</button>
                      <button type="button" role="radio" aria-checked={pick.mode === 'share'} className={pick.mode === 'share' ? 'is-on' : ''} onClick={() => patchAddon(a.id, { mode: 'share' })}>Mijn addon delen</button>
                    </div>
                    {pick.mode === 'recommend' ? (
                      <label className="inv-setup">
                        <small className="subtle">Configuratiepagina (hier vult de genodigde zijn eigen Real-Debrid/TorBox-sleutel in)</small>
                        <input className="text-input" type="url" value={pick.setupUrl ?? ''} onChange={(e) => patchAddon(a.id, { setupUrl: e.target.value || null })} placeholder="https://…/configure" />
                      </label>
                    ) : (
                      <p className="subtle hint inv-warn">De genodigde gebruikt jouw adres, inclusief jouw debrid-sleutel. Alleen doen voor mensen op hetzelfde adres (zelfde IP), anders kan je debrid-account geblokkeerd worden.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Maken…' : 'Link maken'}</button>
        <button className="btn" type="button" onClick={onCancel}>Annuleren</button>
      </div>
    </form>
  );
}
